import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ConfigService } from '../../core/services/config.service';

export interface NotaDetalheChave {
  filial: string;
  doc: string;
  serie: string;
  fornec: string;
  loja: string;
  // Desempate de numeracao repetida: prefeituras reiniciam a numeracao da
  // NFS-e por exercicio, entao o mesmo numero pode existir em anos diferentes
  // para o mesmo fornecedor. Sem estes dois campos o backend casava a nota
  // ANTIGA (SF1) e o detalhe exibia valor/processo/TES da outra nota.
  emissao?: string;            // ISO yyyy-mm-dd
  origem?: 'pendente' | 'entregue';
  // Flags da tela. A TES de NFS-e depende delas e o backend so as considera
  // quando recebe; sem isso ele calcula com 'gera financeiro = nao' e a
  // excecao por filial (que exige financeiro) nunca sai.
  fin?: boolean;
  issRet?: boolean;
}

// Opcao de TES para CTe (frete). Resolvida pelo backend a partir da
// planilha REGRAS CTE. `dup` = gera duplicata; o front filtra por ele
// conforme o campo "Gera Financeiro".
export interface CteTesOpcao {
  tes: string;
  desc: string;
  dup: boolean;
}

export interface NotaDetalheResp {
  filial: string;
  nota: string;
  serie: string;
  fornecedor: string;
  loja: string;
  valor: number;
  emissao: string;
  dataImportada: string;
  especie: string;
  processo: string;
  cnae: string;
  cnaeNorm: string;
  emit?: {
    cnpj: string;
    cnpjFmt?: string;
    razao: string;
    fant: string;
    end: {
      lgr: string;
      nro: string;
      compl: string;
      bairro: string;
      mun: string;
      uf: string;
      cep: string;
      pais: string;
    };
  };
  tmdr?: {
    cnpj: string;
    cnpjFmt?: string;
    razao: string;
    fant: string;
    end: {
      lgr: string;
      nro: string;
      compl: string;
      bairro: string;
      mun: string;
      uf: string;
      cep: string;
      pais: string;
    };
  };
  observacoes: string;
  observacoesAdicionais: string;
  tipoProcesso: string;
  bu: string;
  centroCusto: string;
  priCod?: string;
  tes: number;
  // Descricao da TES no cadastro da filial (SF4). A lista de NFS-e do select
  // e fixa no componente e nao conhece as excecoes por filial do de-para; com
  // a descricao ele acrescenta a TES calculada quando ela nao esta na lista.
  tesDesc?: string;
  natureza: string;
  produto: string;
  valorIr: number;
  valorPis: number;
  valorCofins: number;
  valorCsll: number;
  valorInss: number;
  issRet: boolean;
  fin: boolean;
  // Simples Nacional do emitente (tag <indSN> do XML); usado nas regras de CTe.
  simplesNacional?: boolean;
  // ICMS isento no CTe (grupo <ICMS45> CST 40/41 no XML); o autoPickTesCte
  // prefere a TES ISENTO (003/004) quando .T.
  icmsIsento?: boolean;
  // Pedagio do CTe (tag <comp><xNome>PEDAGIO</xNome><vComp>); >0 dispara
  // deducao de D1_VUNIT/D1_TOTAL e grava F1_VALPEDG na aprovacao.
  valorPedagio?: number;
  // TES validas para CTe (vazio quando a nota nao e CTe).
  tesCteOpcoes?: CteTesOpcao[];
  // .T. quando fornecedor esta em ZOE (oculto). Libera o requisito de
  // processo obrigatorio na tela de aprovacao.
  fornecedorOculto?: boolean;
  // Conta Contabil gravada na SD1 (D1_CONTA). Exibida na sidebar de
  // notas entregues de fornecedor oculto - mesmo padrao de bu/cc.
  contaContabil?: string;
  // .T. quando especie=CTe e o CNPJ do tomador esta na lista DAAS
  // (U_PnfDaasTomador). Frontend usa pra exibir o radio "Sem processo"
  // (Sim/Nao) - quando o operador marca Sim, payload de aprovar envia
  // semProcesso=true e o backend forca TES 403 + natureza 293014.
  tomadorDaas?: boolean;
  // Habilita a opcao "Sem processo" para esta nota. Vem do backend como
  // privilegio SEMPROC (menu /notas-list) OU CTe de tomador DAAS - mesmo
  // gate aplicado em notasPendentes:aprovaUmaCore. Chamado 00017826.
  podeSemProcesso?: boolean;
  // Vencimento do titulo (ISO yyyy-mm-dd). So' vem quando a nota ja gerou
  // financeiro - readNotaSF1 le de SE2.E2_VENCTO (TOP 1 parcela).
  vencimento?: string;
  // Historico de acoes da nota (ZHN, lido por U_PnfHistLista). Ordenado
  // por data + hora DESC. data eh "DD/MM/YYYY HH:MM:SS"; descricao ja vem
  // formatada "acao - desc (usuario)" pra exibicao direta no po-table.
  historico?: HistoricoItemResp[];
  // Situacao da integracao da NFS-e com o Conexos (job PnfNfsJob).
  // disponivel=false quando os campos de controle ainda nao existem no
  // dicionario - nesse caso a tela nao mostra o bloco.
  integracaoConexos?: IntegracaoConexosResp;
}

export interface IntegracaoConexosResp {
  disponivel: boolean;
  status: string;      // '' | 1 | 2 | 3 | 4 | 5 | 9
  statusDes: string;   // texto do status
  documento: string;   // documento criado no Conexos
  tentativas: number;
  mensagem: string;    // ultima mensagem, em linguagem de negocio
  data: string;        // ISO yyyy-mm-dd da ultima tentativa
  hora: string;
  integrada: boolean;
  podeReproc: boolean;
}

export interface HistoricoItemResp {
  data: string;       // "DD/MM/YYYY"
  hora: string;       // "HH:MM:SS"
  acao: string;       // ZOH_ACAO crua (INCLUIU, APROVOU, etc.)
  acaoLabel: string;  // ZOH_ACAO traduzida para label amigavel (UI)
  descricao: string;  // ZOH_DESCRI - detalhes/contexto (ex.: arquivo, motivo)
  usuario: string;    // ZOH_USER (login portal)
  usuarioNom: string; // ZOH_USRNOM (nome cheio - ZOA_NAME)
  obs: string;        // ZOH_OBS (motivo de reprovacao, observacao livre)
}

export interface ResolverProcessoReq {
  processo: string;
  ir?: number;
  pis?: number;
  cof?: number;
  csll?: number;
  inss?: number;
  total?: number;
  issRet?: boolean;
  fin?: boolean;
  // Necessarios para reavaliar as TES de CTe ao trocar o processo.
  filial?: string;
  sn?: boolean;
  // Override de tipoProcesso usado pelo backend como fallback quando o
  // processo nao eh encontrado em SW2 (cenarios de teste ou processo
  // legado nao cadastrado). Quando SW2 acha, ignora o override.
  tipoProcesso?: string;
}

export interface ResolverProcessoResp {
  processo: string;
  tipoProcesso: string;
  bu: string;
  centroCusto: string;
  priCod?: string;
  tes: number;
  tesDesc?: string;
  natureza: string;
  valorIr: number;
  valorPis: number;
  valorCofins: number;
  valorCsll: number;
  valorInss: number;
  issRet: boolean;
  fin: boolean;
  // Status do processo: existe na SW2? esta encerrado (W6_DT_ENCE preenchido)?
  processoExiste?: boolean;
  processoEncerrado?: boolean;
  dataEncerramento?: string;   // ISO yyyy-mm-dd
  // TES de CTe reavaliadas para o novo processo.
  tesCteOpcoes?: CteTesOpcao[];
}

/**
 * Operacao AOL da Logistica Matriz (005003001): fornecedores cujo CTe usa a
 * TES 424 (com credito de PIS/COFINS e com ICMS) em vez da 411, que e o
 * padrao da filial. Confirmado pela fiscal em 22/09/2026, chamado 00018371:
 * ONEXLOG, SOMAA e LOG2X usaram a 424 em 21 de 21 CTe de 2026 nessa filial.
 *
 * E apenas uma SUGESTAO. O criterio real e a finalidade do frete, que so quem
 * envia o CTe conhece, entao o select fica editavel - pode entrar outro
 * fornecedor na AOL e um destes tres pode mandar CTe de outra operacao.
 */
export const FORNECEDORES_AOL_MATRIZ = ['47112002', '35290730', '32755722'];

/** Filial da Logistica Matriz, onde a regra AOL acima se aplica. */
export const FILIAL_LOG_MATRIZ = '005003001';

/** TES da operacao AOL na Logistica Matriz. */
export const TES_AOL_MATRIZ = '424';

/**
 * Devolve a TES sugerida pela operacao AOL, ou vazio quando a regra nao se
 * aplica (outra filial, outro fornecedor, ou a 424 fora das opcoes vindas do
 * backend). Usada pelas duas telas que aprovam CTe.
 */
export function tesSugeridaAol(
  filial: string,
  fornecedor: string,
  opcoes: CteTesOpcao[]
): string {
  if ((filial || '').trim() !== FILIAL_LOG_MATRIZ) { return ''; }
  if (!FORNECEDORES_AOL_MATRIZ.includes((fornecedor || '').trim())) { return ''; }
  return opcoes.some(o => o.tes === TES_AOL_MATRIZ) ? TES_AOL_MATRIZ : '';
}

export interface AprovarNotaItem {
  filial: string;
  numero: string;
  serie: string;
  fornecedor: string;
  loja: string;
  numeroNf?: string;
  processo?: string;
  geraFinanceiro?: string;
  vencimento?: string;
  tipoOperacao?: string;
  produto?: string;
  bu?: string;
  /** Fluxo fornecedor oculto: CC informado direto pelo usuario (sem processo). */
  centroCusto?: string;
  /** Fluxo fornecedor oculto: conta contabil escolhida pelo usuario. */
  contaContabil?: string;
  /** Flag generico de retencao (lOutRet no U_PnfTesNF). */
  retencao?: boolean;
  /** Pedagio do CTe (tag <comp><xNome>PEDAGIO</xNome><vComp>...). Backend
   *  grava em F1_VALPEDG e deduz de D1_VUNIT/D1_TOTAL na aprovacao. */
  valorPedagio?: number;
  /** Flag CTe Sem Processo DAAS (regra 2026-05-27): true quando operador
   *  marcou "Sem processo = Sim" na UI. Backend valida filial DAAS via
   *  U_PnfDaasTomador e forca TES 403 + natureza 293014. */
  semProcesso?: boolean;
}

export interface AprovarReq {
  notas: AprovarNotaItem[];
}

export interface AprovarResultado {
  numero: string;
  ok: boolean;
  doc?: string;
  erro?: string;
  // Serie efetivamente usada na entrega (MV_PNFSER escalona S1 -> S2 -> S3
  // quando o numero ja existe para o fornecedor). Necessaria na chave do
  // anexo (ZOF) e exibida ao operador.
  serie?: string;
  // Preenchido quando houve escalonamento de serie.
  aviso?: string;
}

export interface AprovarResp {
  success: boolean;
  totalOk: number;
  totalErr: number;
  results: AprovarResultado[];
}

export interface RollbackReq {
  filial: string;
  doc: string;
  serie: string;
  fornec: string;
  loja: string;
  anexosRecnos: number[];
}

export interface RollbackResp {
  success: boolean;
  sf1Deletada?: boolean;
  anexosDeletados?: string[];
  warnings?: string[];
  message?: string;
}

export interface ReprovarPendenteReq {
  filial: string;
  doc: string;
  serie: string;
  fornec: string;
  loja: string;
  motivo?: string;
}

export interface ReprovarPendenteResp {
  success: boolean;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class NotaDetalheService {
  constructor(private http: HttpClient, private config: ConfigService) {}

  private base(): string {
    return `${this.config.getHostRest()}/portalnf`;
  }

  aprovar(req: AprovarReq): Observable<AprovarResp> {
    return this.http.post<AprovarResp>(`${this.base()}/notas-pendentes/aprovar`, req);
  }

  // Desfaz a aprovacao: deleta anexos do GCS+ZOF e exclui a SF1 via MATA103 op 5.
  // Usado quando o upload de anexos falha apos a aprovacao OK.
  rollback(req: RollbackReq): Observable<RollbackResp> {
    return this.http.post<RollbackResp>(`${this.base()}/notas-pendentes/rollback`, req);
  }

  // Reprova/exclui a pre-nota das tabelas de staging (SDS+SDT+CKO).
  // Soft delete via D_E_L_E_T_ = '*'. Distinto do reprovar de notas-entregues
  // (que exclui SF1 via MATA103 op 5).
  reprovarPendente(req: ReprovarPendenteReq): Observable<ReprovarPendenteResp> {
    return this.http.post<ReprovarPendenteResp>(`${this.base()}/notas-pendentes/reprovar`, req);
  }

  detalhe(chave: NotaDetalheChave): Observable<NotaDetalheResp> {
    let params = new HttpParams()
      .set('filial', chave.filial)
      .set('doc',    chave.doc)
      .set('serie',  chave.serie)
      .set('fornec', chave.fornec)
      .set('loja',   chave.loja);
    // Opcionais: o backend so aplica o filtro quando recebe (mantem
    // compatibilidade com chamadas antigas / URL sem os queryParams).
    if (chave.emissao) { params = params.set('emissao', chave.emissao); }
    if (chave.origem)  { params = params.set('origem',  chave.origem); }
    if (chave.fin    != null) { params = params.set('fin',    `${chave.fin}`); }
    if (chave.issRet != null) { params = params.set('issRet', `${chave.issRet}`); }
    return this.http.get<NotaDetalheResp>(`${this.base()}/notas-pendentes/detalhe`, { params });
  }

  resolverProcesso(req: ResolverProcessoReq): Observable<ResolverProcessoResp> {
    let params = new HttpParams().set('processo', req.processo);
    if (req.ir     != null) { params = params.set('ir',     `${req.ir}`); }
    if (req.pis    != null) { params = params.set('pis',    `${req.pis}`); }
    if (req.cof    != null) { params = params.set('cof',    `${req.cof}`); }
    if (req.csll   != null) { params = params.set('csll',   `${req.csll}`); }
    if (req.inss   != null) { params = params.set('inss',   `${req.inss}`); }
    if (req.total  != null) { params = params.set('total',  `${req.total}`); }
    if (req.issRet != null) { params = params.set('issRet', `${req.issRet}`); }
    if (req.fin    != null) { params = params.set('fin',    `${req.fin}`); }
    if (req.filial)         { params = params.set('filial', req.filial); }
    if (req.sn     != null) { params = params.set('sn',     `${req.sn}`); }
    // Override de tipoProcesso usado como fallback pelo backend quando o
    // processo nao existe em SW2 (cenarios de teste). Sem isso o backend
    // cai em cTipoProc='' -> U_PnfTesCte usa 'C' default e devolve poucas
    // (ou nenhuma) opcoes; o override permite forcar 'encomenda'/'propria'.
    if (req.tipoProcesso)   { params = params.set('tipoProcesso', req.tipoProcesso); }
    return this.http.get<ResolverProcessoResp>(`${this.base()}/processo/resolver`, { params });
  }
}
